import { defineStore } from 'pinia';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import { DocumentEndPoint } from '@/modules/document/enums/document-end-point.enum.ts';
import type { GanttItemDto } from '@/modules/viewer/dto/gantt-item.dto.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';

type RawDocGanttResponse = { data: ItemsDto<GanttItemDto> };

type DocGanttState = {
  items: GanttItemDto[];
  count: number | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: unknown;
  controller: AbortController | null;
};

export const useDocGanttStore = defineStore(StoreId.DocGantt, {
  state: (): DocGanttState => ({
    items: [],
    count: null,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    controller: null,
  }),
  getters: {
    list: (state) => state.items,
  },
  actions: {
    async fetch(params: Record<string, unknown>) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.isLoading = true;
      this.isLoadingMore = false;

      try {
        const response = await GatewayApi.execute<RawDocGanttResponse>(DocumentEndPoint.GetGanttItems, {
          params: params as Record<string, string | number | boolean | null>,
          signal: controller.signal,
          mapData: false,
        });

        if (this.controller !== controller) {
          return;
        }

        this.items = [...response.data.items];
        if (Number.isInteger(response.data.count)) {
          this.count = response.data.count;
        }
        this.error = null;
      } catch (error) {
        if (this.controller !== controller) {
          return;
        }
        if (ErrorUtil.isForbidden(error) || ErrorUtil.isBusinessError(error)) {
          this.error = error;
          return;
        }
        if (ErrorUtil.isUnexpected(error)) {
          this.error = error;
          throw error;
        }
      } finally {
        if (this.controller === controller) {
          this.isLoading = false;
          this.controller = null;
        }
      }
    },
    async fetchMore(params: Record<string, unknown>) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.isLoadingMore = true;
      this.isLoading = false;

      try {
        const response = await GatewayApi.execute<RawDocGanttResponse>(DocumentEndPoint.GetGanttItems, {
          params: params as Record<string, string | number | boolean | null>,
          signal: controller.signal,
          mapData: false,
        });

        if (this.controller !== controller) {
          return;
        }

        this.items = [...this.items, ...response.data.items];
        if (Number.isInteger(response.data.count)) {
          this.count = response.data.count;
        }
        this.error = null;
      } catch (error) {
        if (this.controller !== controller) {
          return;
        }
        if (ErrorUtil.isForbidden(error) || ErrorUtil.isBusinessError(error)) {
          this.error = error;
          return;
        }
        if (ErrorUtil.isUnexpected(error)) {
          this.error = error;
          throw error;
        }
      } finally {
        if (this.controller === controller) {
          this.isLoadingMore = false;
          this.controller = null;
        }
      }
    },
    abort() {
      this.controller?.abort();
      this.isLoading = false;
      this.isLoadingMore = false;
      this.controller = null;
    },
    clear() {
      this.abort();
      this.items = [];
      this.count = null;
      this.error = null;
      this.isLoadingMore = false;
    },
  },
});
